import { DateFields, DateRange } from '../entities/search/date-range.entity';

const GetTimePart = (isDayStart: boolean): string => {
    return isDayStart ? `00:00:00.000` : `23:59:59.999`;
};

export const PadLeft = (value: number | null, length: number): string | null => {
    if (value === null) {
        return null;
    }

    return value.toString().padStart(length, '0');
};

const GetLastDayOfMonth = (year: string, month: string): string => {
    return PadLeft(new Date(Number.parseInt(year, 10), Number.parseInt(month, 10), 0).getDate(), 2) || '01';
};

const GetWildcardOrISOStringFromDateFields = (fields: DateFields, isStart: boolean): string => {
    const year: string | null = PadLeft(fields.Year, 4);

    if (!year) {
        return '*';
    }

    const month: string = PadLeft(fields.Month, 2) || (isStart ? '01' : '12');
    const day: string = PadLeft(fields.Day, 2) || (isStart ? '01' : GetLastDayOfMonth(year, month));
    return new Date(`${year}-${month}-${day}T${GetTimePart(isStart)}`).toISOString();
};

export const CreateDateRangeQuery = (range: DateRange): string => {
    const fromPart: string = GetWildcardOrISOStringFromDateFields(range.From, true);
    const untilPart: string = GetWildcardOrISOStringFromDateFields(range.Until, false);

    return `[${fromPart} TO ${untilPart}]`;
};

const GetLocalDateFieldsFromISOString = (isoString: string): DateFields => {
    const isoDate = new Date(isoString);

    return {
        Year: isoDate.getFullYear(),
        Month: isoDate.getMonth() + 1,
        Day: isoDate.getDate(),
        Date: isoDate
    };
};

const ParseDateFields = (dateField?: string): DateFields => {
    const starOrISOString: string | undefined = dateField?.trim();
    if (!starOrISOString || starOrISOString === '*') {
        return {
            Year: null,
            Month: null,
            Day: null,
            Date: null
        };
    }

    return GetLocalDateFieldsFromISOString(starOrISOString);
};

export const ParseDateRangeQuery = (query?: string | null): DateRange => {
    if (!query) {
        return {
            From: {
                Year: null,
                Month: null,
                Day: null,
                Date: null
            },
            Until: {
                Year: null,
                Month: null,
                Day: null,
                Date: null
            },
            LengthInDays: Number.NaN
        };
    }

    const fromUntil: string[] = query.replace(/\[|]|{|}/g, '').split('TO');

    const fromPartISO: string | undefined = fromUntil[0] && fromUntil[0].trim();
    const untilPartISO: string | undefined = fromUntil[1] && fromUntil[1].trim();

    const from = ParseDateFields(fromPartISO);
    const until = ParseDateFields(untilPartISO);
    let length = Number.NaN;
    if (from.Date != null && until.Date != null) {
        const diff = Math.abs(from.Date.getTime() - until.Date.getTime());
        length = Math.ceil(diff / (1000 * 3600 * 24));
    }

    return {
        From: from,
        Until: until,
        LengthInDays: length
    };
};

export const IsDateRangeQuery = (query?: string) => {
    return IsLaxDateRangeQuery(query);
};

export function IsStrictDateRangeQuery(query?: string): boolean {
    if (!query) {
        return false;
    }
    // Strict does _not_ allow { or } at the end/start of range, only [ and ]
    return !!query.match(/^[\[][0-9:*T\.Z-]+ TO [0-9:*T\.Z-]+[\]]$/);
}

export function IsLaxDateRangeQuery(query?: string): boolean {
    if (!query) {
        return false;
    }
    // Lax also allows { and } at end/start of range
    return !!query.match(/^[\{\[][0-9:*T\.Z-]+ TO [0-9:*T\.Z-]+[\]\}]$/);
}
